(() => {
  const SELECTOR_ROOT = ".mowow-spec-compare";

  const safeParseJSON = (text) => {
    try {
      return JSON.parse(text);
    } catch (_e) {
      return null;
    }
  };

  const initCompare = (root) => {
    if (!root || root.dataset.specCompareInit === "true") return;
    root.dataset.specCompareInit = "true";

    const mode = (root.dataset.mode || "").trim();

    const toggle = root.querySelector("[data-compare-toggle]");
    const variantSelects = root.querySelectorAll("[data-variant-select]");
    const scrollWrap = root.querySelector("[data-compare-scroll-wrap]");
    const scrollLeftBtn = root.querySelector('[data-compare-scroll="left"]');
    const scrollRightBtn = root.querySelector('[data-compare-scroll="right"]');
    const mobileSelects = root.querySelectorAll("[data-mobile-column-select]");
    const stickyScroll = root.querySelector("[data-compare-sticky-scroll]");
    const stickyBar = root.querySelector("[data-compare-sticky-bar]");
    const container = root.querySelector(".mowow-spec-compare__container");
    const mobileMQ = window.matchMedia("(max-width: 900px)");

    // Optional: map of variantId -> {label, image, price_formatted, ...}
    // Only rendered in products mode.
    let variantMap = null;
    const variantMapEl = root.querySelector("[data-msc-variant-map]");
    if (variantMapEl) {
      variantMap = safeParseJSON(variantMapEl.textContent || "");
    }

    let rafId = null;

    /**
     * Desktop layout requirement:
     * - Total section width is split into 4 equal parts.
     * - 1/4 for the label/spec column.
     * - Remaining 3/4 for the *visible* product columns.
     * - If there are more columns than visible (>=4 total), horizontal scroll kicks in.
     */
    const syncRuntimeWidths = () => {
      if (!container || !scrollWrap) return;

      // On mobile we fully override widths in CSS; avoid fighting it.
      if (mobileMQ.matches) {
        container.style.removeProperty("--msc-label-width-runtime");
        container.style.removeProperty("--msc-col-width-runtime");
        return;
      }

      const baseWidth = scrollWrap.clientWidth;
      if (!Number.isFinite(baseWidth) || baseWidth <= 0) return;

      const visibleCols = Math.max(1, parseInt(root.dataset.visibleColumns || "3", 10) || 3);

      const labelW = Math.round(baseWidth * 0.25);
      const colW = Math.round((baseWidth - labelW) / visibleCols);

      container.style.setProperty("--msc-label-width-runtime", `${labelW}px`);
      container.style.setProperty("--msc-col-width-runtime", `${colW}px`);
    };

    const updateColumnMedia = (colIndex, variantId) => {
      if (!variantMap || !variantId || !(variantId in variantMap)) return;
      const data = variantMap[variantId];
      if (!data || typeof data !== "object") return;

      // Image
      root
        .querySelectorAll(`[data-compare-image][data-compare-col="${colIndex}"]`)
        .forEach((img) => {
          if (!(img instanceof HTMLImageElement)) return;
          if (data.image) img.src = data.image;
          if (data.label) img.alt = data.label;
        });

      // Price
      root
        .querySelectorAll(`[data-compare-price][data-compare-col="${colIndex}"]`)
        .forEach((el) => {
          if (data.price_formatted) {
            el.textContent = `${data.price_formatted}`;
          }
        });
    };

    const updateColumnValues = (select) => {
      const column = select.closest("[data-compare-column]");
      if (!column) return;

      const colIndex = column.dataset.compareCol;
      if (colIndex === undefined) return;

      const variantId = `${select.value || ""}`;
      column.dataset.currentVariantId = variantId;

      // Swap visible value inside all cells of this column
      root
        .querySelectorAll(`[data-compare-cell][data-compare-col="${colIndex}"]`)
        .forEach((cell) => {
          cell.querySelectorAll("[data-variant-value]").forEach((span) => {
            const isActive = span.dataset.variantId === variantId;
            span.classList.toggle("is-active", isActive);
            span.classList.toggle("is-hidden", !isActive);
          });
        });

      // Sync any duplicate selects inside the same column (e.g. desktop select + hidden mobile select)
      root
        .querySelectorAll(`[data-compare-column][data-compare-col="${colIndex}"] [data-variant-select]`)
        .forEach((otherSelect) => {
          if (otherSelect !== select && otherSelect.value !== variantId) {
            otherSelect.value = variantId;
          }
        });

      // Sticky bar title
      if (select.selectedIndex >= 0) {
        const option = select.options[select.selectedIndex];
        if (option) {
          root
            .querySelectorAll(`[data-sticky-title][data-compare-col="${colIndex}"]`)
            .forEach((stickyTitle) => {
              stickyTitle.textContent = option.textContent.trim();
            });
        }
      }

      // Update image/price in header (products mode)
      updateColumnMedia(colIndex, variantId);
    };

    const getVisibleValues = (row) => {
      const values = [];
      row.querySelectorAll("[data-compare-value]").forEach((el) => {
        // Note: .is-hidden is on the parent span for variant values; use layout check.
        if (el.classList.contains("is-hidden")) return;
        if (el.offsetParent === null) return;
        const text = el.textContent.trim();
        values.push(text === "-" ? "" : text);
      });
      return values;
    };

    const syncGroupVisibility = () => {
      root.querySelectorAll("[data-compare-group-title]").forEach((row) => {
        const groupId = row.dataset.groupId;
        const groupRows = root.querySelectorAll(`[data-compare-row][data-group-id="${groupId}"]`);
        const anyVisible = Array.from(groupRows).some(
          (groupRow) => !groupRow.classList.contains("is-hidden")
        );
        row.classList.toggle("is-hidden", !anyVisible);
      });
    };

    const applyDifferences = () => {
      const hideIdentical = !!(toggle && toggle.checked);
      const rows = root.querySelectorAll("[data-compare-row]");

      rows.forEach((row) => {
        if (!hideIdentical) {
          row.classList.remove("is-hidden");
          return;
        }

        const values = getVisibleValues(row);
        if (values.length < 2) {
          row.classList.remove("is-hidden");
          return;
        }

        const nonEmpty = values.filter((val) => val !== "");
        if (nonEmpty.length === 0) {
          row.classList.add("is-hidden");
          return;
        }

        const first = nonEmpty[0];
        const allSame = nonEmpty.length === values.length && nonEmpty.every((val) => val === first);
        row.classList.toggle("is-hidden", allSame);
      });

      syncGroupVisibility();
    };

    // Initialize per-column variant selection (products mode)
    variantSelects.forEach((select) => {
      updateColumnValues(select);
      select.addEventListener("change", () => {
        updateColumnValues(select);
        applyDifferences();
      });
    });

    if (toggle) {
      toggle.addEventListener("change", applyDifferences);
    }

    const updateScrollButtons = () => {
      if (!scrollWrap || !scrollLeftBtn || !scrollRightBtn) return;
      const maxScroll = scrollWrap.scrollWidth - scrollWrap.clientWidth;
      if (maxScroll <= 1) {
        scrollLeftBtn.classList.add("is-hidden");
        scrollRightBtn.classList.add("is-hidden");
        return;
      }
      scrollLeftBtn.classList.remove("is-hidden");
      scrollRightBtn.classList.remove("is-hidden");
      scrollLeftBtn.classList.toggle("is-disabled", scrollWrap.scrollLeft <= 1);
      scrollRightBtn.classList.toggle("is-disabled", scrollWrap.scrollLeft >= maxScroll - 1);
    };

    const getAvailableColumns = () => {
      const cols = new Set();
      root.querySelectorAll("[data-compare-col]").forEach((el) => {
        const idx = parseInt(el.dataset.compareCol || "", 10);
        if (!Number.isNaN(idx)) cols.add(idx);
      });
      return Array.from(cols).sort((a, b) => a - b);
    };

    const parseMobileValue = (raw) => {
      const str = `${raw || ""}`.trim();
      if (!str) return { raw: "", col: Number.NaN, variantId: null };

      // New format: "<colIndex>|<variantId>"
      if (str.includes("|")) {
        const [colRaw, variantId] = str.split("|");
        return {
          raw: str,
          col: parseInt(colRaw || "", 10),
          variantId: variantId || null,
        };
      }

      // Legacy format: "<colIndex>"
      return { raw: str, col: parseInt(str, 10), variantId: null };
    };

    const getAvailableMobileOptions = () => {
      const first = mobileSelects[0];
      if (!first) return [];
      return Array.from(first.options)
        .map((opt) => parseMobileValue(opt.value))
        .filter((opt) => !Number.isNaN(opt.col));
    };

    const applyMobileColumns = (triggerSelect = null) => {
      if (!mobileSelects.length) return;

      const isMobile = mobileMQ.matches;

      // Desktop: show everything
      if (!isMobile) {
        root
          .querySelectorAll("[data-compare-col].is-hidden-mobile")
          .forEach((el) => el.classList.remove("is-hidden-mobile"));

        syncRuntimeWidths();
        applyDifferences();
        return;
      }

      // PRODUCTS MODE: mobile selects pick the variant for the *slot column* (left/right).
      // No column-reordering, no "product column" concept.
      if (mode === "products") {
        // Always render 2 columns on mobile (slot 0 and slot 1). Hide the rest.
        root.querySelectorAll("[data-compare-col]").forEach((el) => {
          const idx = parseInt(el.dataset.compareCol || "", 10);
          if (Number.isNaN(idx)) return;
          el.classList.toggle("is-hidden-mobile", idx > 1);
        });

        // Keep mobile selects in sync with the underlying slot variant selects.
        Array.from(mobileSelects).forEach((mSelect, slotIndex) => {
          const colIndex = slotIndex; // slot 0 -> col 0, slot 1 -> col 1
          const variantSelect = root.querySelector(
            `[data-compare-column][data-compare-col="${colIndex}"] [data-variant-select]`
          );

          if (!variantSelect) return;

          // If we entered mobile (resize), sync the mobile dropdown with the current desktop selection.
          // If the user actively changed THIS dropdown, respect their new value.
          const isUserTriggered = triggerSelect === mSelect;
          if (!isUserTriggered && mSelect.value !== variantSelect.value) {
            mSelect.value = variantSelect.value;
          }

          const desiredVariantId = `${mSelect.value || ""}`;
          if (variantSelect.value !== desiredVariantId) {
            variantSelect.value = desiredVariantId;
          }
          updateColumnValues(variantSelect);
        });

        syncRuntimeWidths();
        applyDifferences();
        updateScrollButtons();
        return;
      }

      // VARIANTS MODE (product page): mobile selects pick which 2 variant *columns* are visible.
      const available = getAvailableColumns();
      if (!available.length) return;

      const allMobileOptions = getAvailableMobileOptions();
      const fallback0 =
        allMobileOptions[0] || { raw: `${available[0]}`, col: available[0], variantId: null };
      const fallback1 =
        allMobileOptions.find((opt) => opt.col !== fallback0.col) ||
        allMobileOptions[0] ||
        {
          raw: `${available[1] ?? available[0]}`,
          col: available[1] ?? available[0],
          variantId: null,
        };

      let selections = Array.from(mobileSelects).map((select) => parseMobileValue(select.value));

      if (Number.isNaN(selections[0].col)) selections[0] = fallback0;
      if (Number.isNaN(selections[1].col)) selections[1] = fallback1;

      // Prevent selecting the same column twice.
      const mobileSelectArr = Array.from(mobileSelects);
      const triggerIndex = triggerSelect ? mobileSelectArr.indexOf(triggerSelect) : -1;

      if (selections.length >= 2 && selections[0].col === selections[1].col) {
        const dupCol = selections[0].col;

        // Revert the dropdown that triggered the duplicate back to its previous value if possible.
        if (triggerIndex >= 0) {
          const prevRaw = triggerSelect.dataset.prevRaw;
          if (prevRaw && triggerSelect.value !== prevRaw) {
            triggerSelect.value = prevRaw;
          } else {
            const alt = allMobileOptions.find((opt) => opt.col !== dupCol);
            if (alt) triggerSelect.value = alt.raw;
          }
        }

        selections = mobileSelectArr.map((s) => parseMobileValue(s.value));
      }

      // Normalize order: slot 0 is leftmost visible.
      if (selections.length >= 2 && selections[0].col > selections[1].col) {
        const tmp = selections[0];
        selections[0] = selections[1];
        selections[1] = tmp;
      }

      if (mobileSelects[0] && `${mobileSelects[0].value}` !== `${selections[0].raw}`) {
        mobileSelects[0].value = selections[0].raw;
      }
      if (mobileSelects[1] && `${mobileSelects[1].value}` !== `${selections[1].raw}`) {
        mobileSelects[1].value = selections[1].raw;
      }

      // Persist current selection to support clean revert.
      mobileSelectArr.forEach((s) => {
        s.dataset.prevRaw = `${s.value}`;
      });

      const selectedSet = new Set([selections[0].col, selections[1].col]);
      root.querySelectorAll("[data-compare-col]").forEach((el) => {
        const idx = parseInt(el.dataset.compareCol || "", 10);
        if (Number.isNaN(idx)) return;
        el.classList.toggle("is-hidden-mobile", !selectedSet.has(idx));
      });

      syncRuntimeWidths();
      applyDifferences();
      updateScrollButtons();
    };

    const updateStickyTop = () => {
      if (!container) return;
      const header =
        document.querySelector("[data-sticky-header]") ||
        document.querySelector(".shopify-section-header") ||
        document.querySelector(".site-header") ||
        document.querySelector(".header") ||
        document.querySelector("header");

      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      if (headerHeight > 0) {
        container.style.setProperty("--msc-sticky-top", `${headerHeight + 10}px`);
      }
    };

    const updateArrowOffset = () => {
      const headerRow =
        root.querySelector(".mowow-spec-compare__head-variants") ||
        root.querySelector(".mowow-spec-compare__head-titles") ||
        root.querySelector("thead tr");

      if (!headerRow) return;
      const height = headerRow.getBoundingClientRect().height;
      if (height > 0) {
        root.style.setProperty("--msc-arrow-offset", `${Math.round(height / 2)}px`);
      }
    };

    const syncStickyScroll = () => {
      if (!stickyScroll || !scrollWrap) return;
      stickyScroll.scrollLeft = scrollWrap.scrollLeft;
    };

    const updateStickyState = () => {
      if (!stickyBar || !container) return;

      const headRow =
        root.querySelector(".mowow-spec-compare__head-titles") ||
        root.querySelector(".mowow-spec-compare__head-variants") ||
        root.querySelector("thead tr");

      if (!headRow) return;

      const titlesRow = root.querySelector(".mowow-spec-compare__head-titles");

      if (mobileMQ.matches) {
        stickyBar.classList.remove("is-visible");
        if (titlesRow) titlesRow.classList.remove("is-hidden-title");
        return;
      }

      syncRuntimeWidths();

      const stickyTop =
        parseFloat(getComputedStyle(container).getPropertyValue("--msc-sticky-top")) || 0;
      const rect = headRow.getBoundingClientRect();

      const rootRect = root.getBoundingClientRect();
      const isRootVisible = rootRect.bottom > stickyTop && rootRect.top < window.innerHeight;

      const shouldStick = rect.top <= stickyTop && isRootVisible;

      const wasVisible = stickyBar.classList.contains("is-visible");
      stickyBar.classList.toggle("is-visible", shouldStick);
      if (titlesRow) titlesRow.classList.toggle("is-hidden-title", shouldStick);

      if (shouldStick && !wasVisible) {
        window.requestAnimationFrame(() => {
          syncStickyScroll();
        });
      }
    };

    if (scrollWrap && scrollLeftBtn && scrollRightBtn) {
      const getColumnWidth = () => {
        const header = scrollWrap.querySelector("th[data-compare-column]:not(.is-hidden-mobile)");
        if (header) return header.getBoundingClientRect().width;

        const cell = scrollWrap.querySelector("td[data-compare-cell]:not(.is-hidden-mobile)");
        if (cell) return cell.getBoundingClientRect().width;

        if (container) {
          const style = getComputedStyle(container);
          const cssWidthRaw =
            style.getPropertyValue("--msc-col-width-runtime") ||
            style.getPropertyValue("--msc-col-width-effective") ||
            style.getPropertyValue("--msc-col-width");

          const cssWidth = parseFloat(cssWidthRaw || "");
          if (!Number.isNaN(cssWidth) && cssWidth > 0) return cssWidth;
        }

        return 200;
      };

      const scrollByColumn = (direction) => {
        const measured = getColumnWidth();
        const step = Math.max(60, Math.round(measured));
        const maxScroll = scrollWrap.scrollWidth - scrollWrap.clientWidth;
        const current = scrollWrap.scrollLeft;
        const index = current / step;
        const targetIndex = direction > 0 ? Math.floor(index) + 1 : Math.ceil(index) - 1;
        const target = Math.min(maxScroll, Math.max(0, targetIndex * step));
        scrollWrap.scrollTo({ left: target, behavior: "smooth" });
      };

      scrollLeftBtn.addEventListener("click", () => scrollByColumn(-1));
      scrollRightBtn.addEventListener("click", () => scrollByColumn(1));

      scrollWrap.addEventListener("scroll", () => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(() => {
          updateScrollButtons();
          updateStickyState();
          syncStickyScroll();
          rafId = null;
        });
      });

      window.addEventListener("resize", () => {
        updateScrollButtons();
        applyMobileColumns();
        updateStickyTop();
        updateArrowOffset();
        syncRuntimeWidths();
        updateStickyState();
      });

      window.addEventListener("scroll", updateStickyState, { passive: true });

      window.addEventListener(
        "load",
        () => {
          syncRuntimeWidths();
          updateArrowOffset();
          updateStickyState();
        },
        { once: true }
      );

      updateScrollButtons();
    }

    if (mobileSelects.length) {
      mobileSelects.forEach((select) => {
        select.dataset.prevRaw = `${select.value}`;
        select.addEventListener("change", () => applyMobileColumns(select));
      });
      applyMobileColumns();
    }

    updateStickyTop();
    syncRuntimeWidths();
    updateArrowOffset();
    updateStickyState();
    applyDifferences();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR_ROOT).forEach(initCompare);
  };

  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
